import { useState } from 'react'
import { Link } from 'react-router'
import type { JSX } from 'react'
import { api, call } from '@/api/client'
import type { Schema } from '@/api/client'
import { useMutation, useQuery } from '@/api/query'
import { idempotencyKey } from '@/lib/idempotency'
import { formatDate } from '@/lib/format'
import { may, useActiveTenant } from '@/tenant/context'
import { Button } from '@/ui/Button'
import { Empty, ErrorState, InlineError, Loading } from '@/ui/states'
import { useToast } from '@/ui/toast-context'
import { isAmount, parseAmount, toAmountField } from './money'

type Usage = Schema<'UsageResponse'>
type Limit = Schema<'LimitOut'>
type Fx = Schema<'FxOut'>
type NextPlan = Schema<'NextPlanOut'>

/**
 * What "Raise the cap for this month" is pre-filled with, as a multiple of the
 * ceiling that stopped the work (ADM-9).
 *
 * **A suggestion, never a commitment.** The mockup shows ₹12,000 in a field
 * whose cap is ₹8,000, and the point of pre-filling it is that a tenant whose
 * runs have stopped can press one button; the field stays editable and the
 * amount is still theirs. Half again is enough headroom to finish a month that
 * has already been paced against the cap, and small enough that accepting it
 * without reading is not how somebody spends four times what they meant to.
 */
const SUGGESTED_RAISE = 1.5

/**
 * The panel-paragraph style `admin#overage` gives every "One-off…" line
 * (ADM-28). `<p class="ar-muted">` on its own inherits the UA's `1em` margins
 * and body 1rem, so the three panels' explanations rendered heavier and further
 * apart than the design's — and the difference compounds down a stack of three.
 */
const PANEL_TEXT = {
  margin: 'var(--ar-space-2) 0',
  fontSize: 'var(--ar-text-body-sm-size)',
} as const

/**
 * `admin#usage` and `admin#overage`, as one screen.
 *
 * One screen rather than two because they are one state machine: the overage
 * prompt is what the usage screen *becomes* when the ceiling is reached, and
 * routing between them would mean a tenant whose runs have stopped has to find
 * a second page to say so.
 *
 * **The prompt's three choices are the approved ones, in the approved order** —
 * raise the cap for this month, move to a bigger plan, or wait for the period
 * to roll. The design's reasoning is quoted on the card because it is the point
 * of the whole feature: *"the default is to stop, not to keep spending. A
 * surprise bill destroys trust far faster than a paused run."*
 *
 * **Every meter and every row is data.** The plan limits come from
 * `subscriptions.entitlements` and the breakdown from whatever meters have
 * activity, each with the label `app/metering/meters.py` gives it — so the day
 * S-B22 meters DataForSEO or S-B25 meters content, the row appears here with no
 * change to this file. A hardcoded table would be stale the first time it
 * mattered.
 */
export function UsagePage(): JSX.Element {
  const { tenant, membership } = useActiveTenant()
  const toast = useToast()
  const manages = may(membership, 'budget:manage')
  // `null` is "untouched", not "empty" — the suggested amount (ADM-9) is only a
  // default until the tenant types, and an initial `''` could not tell the two
  // apart: clearing the field would silently re-fill it from under the caret.
  const [raise, setRaise] = useState<string | null>(null)

  const usage = useQuery((signal) => call(() => api.GET('/api/v1/usage', { signal })), [])

  const grant = useMutation((capMinor: number) =>
    call(() =>
      api.POST('/api/v1/usage/budget/overage', {
        body: { cap_minor: capMinor },
        params: { header: { 'Idempotency-Key': idempotencyKey('overage', tenant.id) } },
      }),
    ),
  )

  if (usage.state.status === 'loading') return <Loading label="Loading usage" lines={5} />
  if (usage.state.status === 'error') {
    return <ErrorState failure={usage.state.failure} onRetry={usage.refetch} />
  }

  const data: Usage = usage.state.data
  const { budget, spend } = data
  // `?? null` throughout: the generated types make every optional field
  // `T | null | undefined`, so a `=== null` check alone leaves `undefined`
  // unnarrowed — the same note `BillingPage` carries about `subscription`.
  const ceiling = budget.effective_display ?? null
  // ADM-9. The pre-filled suggestion, or an empty field when there is no cap to
  // take half again of — and a bare number appearing in an empty field would be
  // a figure from nowhere.
  //
  // **`> 0`, not just "not null".** A cap of zero is a real thing a tenant can
  // set (`SaveBudgetRequest.cap_minor` is `ge=0`) and it pauses everything at
  // once, so this screen is exactly where they arrive — and half of nothing is
  // nothing. `POST /usage/budget/overage` requires `gt=0`, so a suggested "0"
  // is an enabled button that 422s. Empty leaves it disabled until they type,
  // which is the honest state.
  const suggested =
    budget.effective_cap_minor === null ||
    budget.effective_cap_minor === undefined ||
    budget.effective_cap_minor <= 0
      ? ''
      : toAmountField(Math.round(budget.effective_cap_minor * SUGGESTED_RAISE))
  const typed = raise ?? suggested

  async function onRaise(): Promise<void> {
    const amount = parseAmount(typed)
    if (amount === null) return
    const result = await grant.run(amount)
    if (!result.ok) return
    // Back to untouched rather than to empty, so the field re-suggests against
    // the cap that was just raised instead of holding the amount already spent.
    setRaise(null)
    usage.refetch()
    toast.show({ kind: 'success', title: 'Cap raised for this month' })
  }

  return (
    <div className="ar-stack ar-stack--loose">
      {/* The heading alone (parity P-USE-01); the way to the cap is the link
          in the cap row's own caption, where the sentence names it. */}
      <h1 className="ar-h1">Usage this month</h1>

      {/* ADM-27. `admin#overage` is a screen of its own, and its card is centred
          at 38rem. It is rendered inside this page because the prompt is what
          the usage screen *becomes* — but a full-width card is the wrong shape
          for it: this is one decision with three options, and the design's
          narrow column is what makes it read as one. */}
      {spend.paused ? (
        <section className="ar-card" style={{ maxWidth: '38rem', margin: '0 auto', width: '100%' }}>
          <div className="ar-card__body">
            <div className="ar-error-state">
              <span
                className="ar-error-state__icon"
                style={{ background: 'var(--ar-warning-bg)', color: 'var(--ar-warning)' }}
                aria-hidden="true"
              >
                ⚠
              </span>
              <span className="ar-error-state__title">
                You&apos;ve hit your {ceiling ?? 'budget'} cap
              </span>
              <span className="ar-error-state__body">
                Everything is paused — no audits, no content, no outreach. Nothing was left
                half-finished, and nothing has been billed above your cap.
              </span>
            </div>

            <div className="ar-stack" style={{ marginTop: 'var(--ar-space-5)' }}>
              <div className="ar-panel">
                <strong>Raise the cap for this month</strong>
                <p className="ar-muted" style={PANEL_TEXT}>
                  One-off. Reverts to {budget.cap_display ?? 'no cap'} on{' '}
                  {formatDate(`${data.resets_on}T00:00:00Z`)}. Billed at cost + 20%.
                </p>
                <InlineError failure={grant.state.failure} />
                <div className="ar-row">
                  {/* ADM-30. No visible label, as the design draws it: the panel's
                      own heading two lines up says what the field is, and a
                      second `.ar-field__label` saying it again pushed the input
                      out of line with the button beside it. The control is still
                      *named* — `aria-label` carries the same words to a screen
                      reader — because "the mockup has no label" is a statement
                      about ink, not about the accessibility tree. */}
                  <div className="ar-input-group" style={{ maxWidth: '14rem' }}>
                    <input
                      className="ar-input ar-num"
                      inputMode="decimal"
                      aria-label="New cap for this month"
                      disabled={!manages}
                      value={typed}
                      onChange={(event) => {
                        setRaise(event.target.value)
                        grant.reset()
                      }}
                    />
                    <span className="ar-input-group__addon">{budget.currency}</span>
                  </div>
                  <Button
                    tone="primary"
                    disabled={!manages || !isAmount(typed)}
                    busy={grant.state.status === 'running'}
                    onClick={() => {
                      void onRaise()
                    }}
                  >
                    Raise &amp; resume
                  </Button>
                </div>
              </div>

              {/* ADM-10. The design names the tier, its price and its limits, and
                  its button goes straight to it. The app offered "a bigger plan"
                  and "See plans" — the one sentence on this screen a tenant could
                  not act on without leaving it to work out which plan was meant.
                  `next_plan` is absent at the top of the ladder, where the
                  generic wording is the only true one. */}
              <NextPlanPanel next={data.next_plan ?? null} />

              <div className="ar-panel">
                <strong>Or wait</strong>
                {/* ADM-29. The last panel's paragraph loses only its bottom
                    margin — it is the last thing in the card — and keeps the top
                    one the two above it have. */}
                <p className="ar-muted" style={{ ...PANEL_TEXT, marginBottom: 0 }}>
                  Everything resumes automatically on {formatDate(`${data.resets_on}T00:00:00Z`)}.
                  Your rankings and data are untouched in the meantime.
                </p>
              </div>
            </div>
          </div>
        </section>
      ) : null}

      <section className="ar-card">
        <div className="ar-card__header">
          <h2>Plan limits</h2>
          <span className="ar-card__spacer" />
          {/* ADM-24. `.ar-subtle` is a colour, not a size, so this inherited
              body 1rem and read as loud as the h3 beside it. */}
          <span className="ar-subtle" style={{ fontSize: 'var(--ar-text-body-sm-size)' }}>
            Resets {formatDate(`${data.resets_on}T00:00:00Z`)}
          </span>
        </div>
        <div className="ar-card__body ar-stack">
          {data.limits.map((limit) => (
            <Meter key={limit.key} limit={limit} />
          ))}

          <hr />

          <div>
            <div className="ar-row" style={{ justifyContent: 'space-between' }}>
              <span>Usage spend against your cap</span>
              <span className="ar-mono">
                {spend.spend_display}
                {ceiling === null ? '' : ` / ${ceiling}`}
              </span>
            </div>
            <Bar percent={spend.percent} />
            {/* ADM-25. Caption size and a gap above it, as the design draws
                every meter caption. At body 1rem with no margin this ran into
                the bar and outweighed the figure it explains. */}
            <div
              className="ar-subtle"
              style={{
                fontSize: 'var(--ar-text-caption-size)',
                marginTop: 'var(--ar-space-2)',
              }}
            >
              {budget.effective_cap_minor === null ? (
                // Not "the four limits": `data.limits` is two counted rows plus
                // three metered allowances (`app/v1/usage.py:_counted_limits`
                // and `_metered_limits`), so it renders five, and `PlansPage`
                // says five. The count is left out rather than corrected to
                // five, on the same terms as the string below — a number
                // hard-coded beside a `.map()` drifts the moment the list does.
                'No cap set. The limits above come with the plan; everything below is metered rather than capped.'
              ) : (
                <>
                  Your own cap, set in{' '}
                  <Link className="ar-link" to="/settings/budget">
                    Settings → Budget caps
                  </Link>
                  . The limits above come with the plan; this one you choose, and everything below
                  it — rank data, model calls, images, proxy bandwidth — is metered rather than
                  capped by count.
                </>
              )}
            </div>
          </div>
        </div>
      </section>

      <section className="ar-card">
        <div className="ar-card__header">
          <h2>Where the spend went</h2>
        </div>
        {data.breakdown.length === 0 ? (
          <div className="ar-card__body">
            <Empty
              title="Nothing metered yet"
              body="Rank data, model calls and proxy bandwidth appear here as soon as a run spends anything."
            />
          </div>
        ) : (
          <div className="ar-table-wrap" tabIndex={0}>
            <table className="ar-table">
              <thead>
                <tr>
                  <th>Resource</th>
                  <th className="ar-num">Units</th>
                  <th className="ar-num">Cost</th>
                  {/* ADM-8, ADM-26. What the row *is* — the vendor, or why it
                      costs nothing. Three of the eight meters are counted and
                      not billed, and a ₹0 line with no explanation is the row a
                      tenant writes in about. */}
                  <th>Notes</th>
                </tr>
              </thead>
              <tbody>
                {data.breakdown.map((row) => (
                  <tr key={row.meter}>
                    <td>{row.label}</td>
                    <td className="ar-num">
                      {row.quantity.toLocaleString()} {row.unit}
                      {row.quantity === 1 ? '' : 's'}
                    </td>
                    <td className="ar-num">{row.display}</td>
                    <td className="ar-muted">{row.note}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {data.fx ? <FxNote fx={data.fx} /> : null}
      </section>
    </div>
  )
}

/**
 * `admin#overage`'s second choice: the specific plan above this one (ADM-10).
 *
 * **It names the tier or it says nothing specific.** With `next_plan` absent —
 * the tenant is on Scale, or has no subscription at all — the generic wording is
 * the only true one, and inventing "move to a bigger plan" as a link to a ladder
 * whose top rung they already occupy would be worse than the sentence it
 * replaced.
 *
 * The limits it prints are the design's four, in the design's order. Not the
 * fifth and sixth from `PlansPage`: this is an aside on a screen about money,
 * and a tenant who wants the whole comparison has a button to it.
 */
function NextPlanPanel({ next }: { next: NextPlan | null }): JSX.Element {
  if (next === null) {
    return (
      <div className="ar-panel">
        <strong>Or move to a bigger plan</strong>
        <p className="ar-muted" style={PANEL_TEXT}>
          Worth it if you are raising the cap most months anyway — but it does not raise the cap
          itself, which stays yours to set.
        </p>
        <Link className="ar-btn ar-btn--ghost" to="/billing/plans">
          See plans
        </Link>
      </div>
    )
  }
  return (
    <div className="ar-panel">
      <strong>Or move to {next.name}</strong>
      <p className="ar-muted" style={PANEL_TEXT}>
        {next.price_display}/month for {next.sites} {next.sites === 1 ? 'site' : 'sites'},{' '}
        {next.linkedin_accounts} LinkedIn {next.linkedin_accounts === 1 ? 'account' : 'accounts'},{' '}
        {next.messages_per_month.toLocaleString()} messages and {next.articles_per_month} articles.
        Worth it if you are raising the cap most months anyway — but it does not raise the cap
        itself, which stays yours to set.
      </p>
      <Link className="ar-btn ar-btn--ghost" to={`/billing/plans#plan-${next.code}`}>
        See {next.name}
      </Link>
    </div>
  )
}

/**
 * Which exchange rate turned dollar vendor invoices into the rupee figures above.
 *
 * Absent for a USD tenant — nothing was converted — and absent when the
 * deployment holds no usable rate, because a caption is not worth failing a
 * screen over. The numbers in the table were converted when they were recorded
 * and do not depend on this line being drawn.
 *
 * It exists because the first question anybody asks about a rupee figure derived
 * from dollar invoices is *at what rate*, and the second is *says who* — and
 * "the rate on the day" is a different claim from "the rate we have configured".
 * The date shown is the day the source **published**, which over a weekend is
 * the Friday rather than today.
 */
function FxNote({ fx }: { fx: Fx }): JSX.Element {
  // `formatDate`, not a second formatter of its own: every other date on this
  // screen goes through it, and two spellings of "24 Jul 2026" on one page is
  // the sort of thing that reads as two different dates.
  const published = formatDate(fx.as_of)
  return (
    <div className="ar-card__footer ar-subtle">
      {/* One child: the footer is a flex row, and bare text beside a span was
          three flex items, breaking the sentence at the rate (parity P-USE-05). */}
      <span>
        Vendor costs are billed in US dollars and converted at{' '}
        <span className="ar-mono">{Number(fx.rate).toFixed(2)}</span> per dollar —{' '}
        {fx.source === 'configured'
          ? 'a rate set by AugmenTrait'
          : 'the European Central Bank’s published rate'}{' '}
        for {published}.
      </span>
    </div>
  )
}

/** One plan allowance, with the meter S-D3 draws beside it. */
function Meter({ limit }: { limit: Limit }): JSX.Element {
  // `?? null` for the same reason the page does it: an optional field in the
  // generated types is `number | null | undefined`, and only one of those two
  // absences narrows away on a `=== null`.
  const allowance = limit.limit ?? null
  return (
    <div>
      <div className="ar-row" style={{ justifyContent: 'space-between' }}>
        <span>
          {limit.label}
          {limit.window === 'week' ? <span className="ar-subtle"> · this week</span> : null}
        </span>
        <span className="ar-mono">
          {limit.used.toLocaleString()}
          {allowance === null ? '' : ` / ${allowance.toLocaleString()}`}
        </span>
      </div>
      <Bar percent={limit.percent} />
    </div>
  )
}

/**
 * The meter bar. Absent rather than empty when there is no limit: a bar at 0%
 * would say "you have used none of your allowance" where the truth is that no
 * plan states one.
 */
function Bar({ percent }: { percent: number | null | undefined }): JSX.Element | null {
  if (percent === null || percent === undefined) return null
  const width = Math.min(100, Math.max(0, percent))
  return (
    <div
      className="ar-meter app-meter--stacked"
      role="meter"
      aria-valuenow={width}
      aria-valuemin={0}
      aria-valuemax={100}
    >
      <div className="ar-meter__fill" style={{ width: `${String(width)}%` }} />
    </div>
  )
}
